import React, { useState } from "react";
import {
  Drawer,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { motion, AnimatePresence } from "framer-motion";
import "./McqSelectionPage.css";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useDispatch } from "react-redux";
import { selectTechFieldAction } from "../../../actions/Badge/badgeActions";
import { GetTechIcon } from "../../../utils/fetchIcons";

const McqSelectionPage = ({ open, onClose, techFields }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [selectedField, setSelectedField] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : null);
  };

  const handleRadioChange = (fieldId, option, field) => {
    setSelectedField({ fieldId, option });
    dispatch(
      selectTechFieldAction({ fieldId: field?._id, subFieldId: option?._id })
    );
  };

  const handleNext = () => {
    if (selectedField) {
      history.push("/certificates/start-quiz");
      onClose();
    }
  };

  const drawerVariants = {
    hidden: isMobile ? { y: "100%", opacity: 0 } : { x: "100%", opacity: 0 },
    visible: isMobile
      ? {
          y: 0,
          opacity: 1,
          transition: { type: "spring", damping: 25, stiffness: 200 },
        }
      : {
          x: 0,
          opacity: 1,
          transition: { type: "spring", damping: 25, stiffness: 200 },
        },
    exit: isMobile
      ? { y: "100%", opacity: 0, transition: { duration: 0.3 } }
      : { x: "100%", opacity: 0, transition: { duration: 0.3 } },
  };

  const accordionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3,
        ease: "easeOut",
      },
    }),
  };

  const radioVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.2,
      },
    }),
  };

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      className="mcqselection-drawer"
      PaperProps={{
        className: `mcqselection-drawer-paper ${
          isMobile
            ? "mcqselection-drawer-paper--mobile"
            : "mcqselection-drawer-paper--desktop"
        }`,
      }}
    >
      <motion.div
        className="mcqselection-container"
        variants={drawerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {/* Sticky Header */}
        <div className="mcqselection-header">
          <IconButton
            onClick={onClose}
            className="mcqselection-back-button"
            aria-label="Go back"
          >
            <ArrowBackIcon />
          </IconButton>
          <h2 className="mcqselection-title">
            Which field you are interested in?
          </h2>
        </div>

        {/* Scrollable Content */}
        <div className="mcqselection-content">
          <div className="mcqselection-accordion-list">
            {techFields?.fields?.map((field, index) => (
              <motion.div
                key={field?._id}
                custom={index}
                variants={accordionVariants}
                initial="hidden"
                animate="visible"
              >
                <Accordion
                  expanded={expandedAccordion === field?.sub_id}
                  onChange={handleAccordionChange(field?.sub_id)}
                  className="mcqselection-accordion"
                  elevation={0}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className="mcqselection-accordion-summary"
                  >
                    <div className="mcqselection-accordion-header">
                      <div className="mcqselection-icon">
                        {<GetTechIcon tech={field?.sub_id} />}
                      </div>
                      <span className="mcqselection-field-name">
                        {field?.name}
                      </span>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails className="mcqselection-accordion-details">
                    <AnimatePresence>
                      {expandedAccordion === field?.sub_id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="mcqselection-options-container"
                        >
                          <RadioGroup
                            value={
                              selectedField?.fieldId === field?.sub_id
                                ? selectedField?.option?._id
                                : ""
                            }
                            onChange={(e) => {
                              const option = field.technologySubfields.find(
                                (sf) => sf._id === e.target.value
                              );

                              handleRadioChange(field.sub_id, option, field);
                            }}
                          >
                            {field?.technologySubfields?.map(
                              (option, optionIndex) => (
                                <motion.div
                                  key={option?._id}
                                  custom={optionIndex}
                                  variants={radioVariants}
                                  initial="hidden"
                                  animate="visible"
                                  className="mcqselection-option-item"
                                >
                                  <FormControlLabel
                                    value={option?._id}
                                    control={
                                      <Radio className="mcqselection-radio" />
                                    }
                                    label={option?.name}
                                    className="mcqselection-form-label"
                                  />
                                </motion.div>
                              )
                            )}
                          </RadioGroup>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </AccordionDetails>
                </Accordion>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Sticky Footer with Next Button */}
        <div className="mcqselection-footer">
          <motion.div
            whileHover={{ scale: selectedField ? 1.02 : 1 }}
            whileTap={{ scale: selectedField ? 0.98 : 1 }}
          >
            <Button
              variant="contained"
              fullWidth
              disabled={!selectedField}
              onClick={handleNext}
              className={`mcqselection-next-button ${
                selectedField ? "mcqselection-next-button--enabled" : ""
              }`}
            >
              Next
            </Button>
          </motion.div>
        </div>
      </motion.div>
    </Drawer>
  );
};

export default McqSelectionPage;
